/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { fileUrl } from '../api';
import { formatCell, formatValue, isBlank } from '../fieldTypes';

const Value = ({ field, value }) => {
  if (isBlank(value)) return <span className="ipm-muted">—</span>;
  if (field.type === 'images') {
    return (
      <Box className="ipm-images">
        {value.map(id => (
          <Box key={id} className="ipm-image-thumb">
            <a href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" /></a>
          </Box>
        ))}
      </Box>
    );
  }
  if (field.type === 'table') {
    const cols = field.columns || [];
    return (
      <Table size="small" sx={{ maxWidth: 600 }}>
        <TableHead><TableRow>{cols.map(c => <TableCell key={c.id}>{c.label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {value.map((row, i) => (
            <TableRow key={i}>{cols.map(c => <TableCell key={c.id}>{formatCell(c, row[c.id]) || '—'}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  if (field.type === 'files') {
    return value.map(fl => <div key={fl.id}><a href={fileUrl(fl.id)} target="_blank" rel="noreferrer">{fl.name}</a></div>);
  }
  return <span style={{ whiteSpace: 'pre-wrap' }}>{formatValue(field, value)}</span>;
};

/**
 * Shows an entry exactly as it was saved, using the field snapshot stored with
 * it — so it reads correctly even if the form has changed since.
 */
const EntryDetail = ({ entry, currentFieldIds }) => {
  const groups = [];
  for (const f of entry.fields || []) {
    let g = groups.find(x => x.title === (f.section || ''));
    if (!g) groups.push(g = { title: f.section || '', fields: [] });
    g.fields.push(f);
  }

  return (
    <Box>
      {groups.map(g => (
        <Box key={g.title} sx={{ mb: 2 }}>
          {g.title && <Typography variant="subtitle1" className="ipm-section-title">{g.title}</Typography>}
          <dl className="ipm-detail-list">
            {g.fields.map(f => (
              <React.Fragment key={f.id}>
                <dt>
                  {f.label}
                  {currentFieldIds && !currentFieldIds.has(f.id) && (
                    <Chip size="small" label="removed from form" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                  )}
                </dt>
                <dd><Value field={f} value={entry.data?.[f.id]} /></dd>
              </React.Fragment>
            ))}
          </dl>
        </Box>
      ))}
      <Typography variant="body2" color="text.secondary">
        Entered by {entry.created_by?.name || 'unknown'} on {new Date(entry.createdAt).toLocaleString()}
        {entry.updated_by?.name && entry.updatedAt !== entry.createdAt &&
          ` · last edited by ${entry.updated_by.name} on ${new Date(entry.updatedAt).toLocaleString()}`}
        {` · form version ${entry.form_version}`}
      </Typography>
    </Box>
  );
};

export default EntryDetail;
